import { CookiesConsentType } from 'types/CookiesConsentType';
import { getPrivacyOptOut } from 'utils/privacyOptOut';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export type ConsentModeValue = 'granted' | 'denied';

export type ConsentModeObject = {
  analytics_storage: ConsentModeValue;
  ad_storage: ConsentModeValue;
  ad_user_data: ConsentModeValue;
  ad_personalization: ConsentModeValue;
  functionality_storage: ConsentModeValue;
  personalization_storage: ConsentModeValue;
  security_storage: ConsentModeValue;
};

// Single source of truth for the Brij-banner -> Google Consent Mode mapping.
// Used by BOTH the gtag consent update below and the event-level snapshot
// (utils/getCookieConsent.ts) so what Google is told and what events carry
// can never drift apart.
export const mapCookiesConsentToConsentMode = (
  consent: CookiesConsentType
): ConsentModeObject => ({
  analytics_storage: consent.analytics ? 'granted' : 'denied',
  ad_storage: consent.marketing ? 'granted' : 'denied',
  ad_user_data: consent.marketing && consent.analytics ? 'granted' : 'denied',
  ad_personalization: consent.marketing ? 'granted' : 'denied',
  functionality_storage: consent.functional ? 'granted' : 'denied',
  personalization_storage: consent.social ? 'granted' : 'denied',
  security_storage: 'granted',
});

// A GPC or Do Not Sell opt-out keeps the ad trio denied whatever the banner
// decision or regional default says; every other signal is untouched.
const applyOptOutToConsentMode = (
  consentMode: ConsentModeObject
): ConsentModeObject => {
  try {
    if (!getPrivacyOptOut().active) return consentMode;
    return {
      ...consentMode,
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    };
  } catch (error) {
    console.error('CONSENT MODE OPT-OUT CLAMP FAILED: ', error);
    return consentMode;
  }
};

export const updateConsentMode = (consent: CookiesConsentType) => {
  if (typeof window.gtag === 'function') {
    window.gtag(
      'consent',
      'update',
      applyOptOutToConsentMode(mapCookiesConsentToConsentMode(consent))
    );
  }
};

// Do Not Sell click, or a standing opt-out replayed on load: deny the ad trio
// only, with no other keys, so analytics and the rest stay as they are.
export const applyDoNotSellToConsentMode = () => {
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
    }
  } catch (error) {
    console.error('DO NOT SELL CONSENT MODE UPDATE FAILED: ', error);
  }
};

export const triggerConsentEvent = () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'brij-cookies-initiation',
  });
};

export const pushDoNotSellEvent = () => {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'brij-do-not-sell',
    });
  } catch (error) {
    console.error('DO NOT SELL DATALAYER PUSH FAILED: ', error);
  }
};
